import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { Seo } from "@/client/components/seo";

import { FAQSection } from "./sections/faq";
import { FeaturesSection } from "./sections/features";
import { HeroSection } from "./sections/hero";
import { LogoCloudSection } from "./sections/logo-cloud";
import { HomeServices } from "./sections/services";
import { TemplatesSection } from "./sections/templates";
import { TestimonialsSection } from "./sections/testimonials";

export const HomePage = () => {
  const { i18n } = useLingui();

  // FAQ rich-results schema, kept in the page language and matching the visible
  // FAQ section below.
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: t`How does Izicvs find jobs for me?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: t`Izicvs pulls real openings from company boards such as Greenhouse, Lever and Ashby and from aggregators like Adzuna and Remotive, then ranks each one against your resume with clear match reasons.`,
        },
      },
      {
        "@type": "Question",
        name: t`How does the AI help me apply?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: t`For each job the AI scores your fit, tailors your resume and writes a cover letter without inventing anything, pre-fills the application form, and flags scam or ghost postings.`,
        },
      },
      {
        "@type": "Question",
        name: t`What does the autonomous agent do?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: t`On the Autopilot plan, Izicvs re-scans your saved searches around the clock, prepares matching applications automatically and sends a daily digest. Nothing is sent without your approval.`,
        },
      },
      {
        "@type": "Question",
        name: t`Can I track my applications?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: t`Yes. Every application lives on a Kanban board from "to apply" to "offer", with drag and drop, duplicate detection, and a link back to each job and its analysis.`,
        },
      },
      {
        "@type": "Question",
        name: t`Is my data secure?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: t`Your data is never sold. AI providers process your content only to generate your results, storage is secure, and you can export or delete your data on request under the GDPR.`,
        },
      },
      {
        "@type": "Question",
        name: t`What does it cost?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: t`Start free with the resume builder, job search and tracker. Pro is 9 EUR per month for the full AI toolkit and Autopilot is 19 EUR per month for the autonomous agent. Cancel anytime.`,
        },
      },
    ],
  };

  return (
    <main className="relative isolate bg-background">
      <Seo
        lang={i18n.locale}
        title={t`Izicvs, the AI resume and cover letter builder`}
        description={t`Izicvs is a modern resume builder that simplifies creating, updating, and sharing professional resumes online.`}
        path="/"
        jsonLd={faqSchema}
      />

      <HeroSection />
      <LogoCloudSection />
      <HomeServices />
      <FeaturesSection />
      <TemplatesSection />
      <TestimonialsSection />
      <FAQSection />
    </main>
  );
};
